import React from "react";
import { useMyAccessProfileV2 } from "../../apis/tryst/get-my-access-profile";
import useGetReceiptConfig from "../../apis/tryst/get-receipt-config";
import { ReactComponent as LoadingCircleIcon } from "../../assets/icons/custom/loading-circle.svg";
import useReceiptCustomisationStore from "../../stores/receipt-customisation/receipt-customisation-store";
import ColumnsComponent from "./columns-component";
import CustomisationHeader from "./customisation-header";
import HeaderAndFooter from "./header-and-footer";
import MarginsComponent from "./margins-component";
import OtherPreferencesComponent from "./other-preferences-component";
import SubmitAndResetComponent from "./submit-and-reset";
import { DateAndTimeConfigForReceipt } from "@elixir/types";

const CustomisationTemplate = ({
  isInsideProfile,
}: {
  isInsideProfile: boolean;
}) => {
  const { data: myAccessProfile } = useMyAccessProfileV2();

  const { clinicId, selectedDoctorId } = useReceiptCustomisationStore();

  // Use selectedDoctorId from store, fallback to myAccessProfile
  const currentDoctorId =
    selectedDoctorId || myAccessProfile?.loginmeta?.docid!;

  const { isLoading, data: receiptConfigData } = useGetReceiptConfig({
    clinic_id: clinicId,
    doctor_oid: currentDoctorId,
  });

  // Load config into store when data changes
  const { setReceiptState } = useReceiptCustomisationStore();

  React.useEffect(() => {
    const config = receiptConfigData?.data?.[0];

    if (config) {
      const {
        margins,
        flags,
        header_image_url,
        heights,
        footer_image_url,
        page_size,
        constraints,
      } = config;

      const {
        print_paymode_split,
        print_receipt_number,
        print_uhid,
        print_header,
        print_footer,
        discount_column,
        quantity_column,
        print_transaction_id,
        print_amount_in_words,
        date_and_time,
        bill_created_at,
        print_bill_created_by,
      } = flags || {};

      // Load actual config values
      setReceiptState({
        margin: margins || { top: 1, bottom: 1, left: 1, right: 1 },
        others: {
          showUHID: print_uhid ?? false,
          showPaymentMethod: print_paymode_split ?? false,
          showReceiptNumber: print_receipt_number ?? true,
          requireUhid: constraints?.require_uhid ?? false,
          print_amount_in_words: print_amount_in_words ?? false,
          date_and_time: date_and_time ?? DateAndTimeConfigForReceipt.DATE_ONLY,
          bill_created_at: bill_created_at ?? false,
          print_bill_created_by: print_bill_created_by ?? false,
        },
        header: {
          headerHeight: heights?.header ?? 2.5,
          showHeader: print_header ?? true,
          headerImageSrc: header_image_url || "",
          headerImageFile: undefined,
        },
        footer: {
          footerHeight: heights?.footer ?? 1.5,
          showFooter: print_footer ?? true,
          footerImageSrc: footer_image_url || "",
          footerImageFile: undefined,
          showPaymentId: print_transaction_id ?? false,
        },
        columns: {
          discount: discount_column || "ALWAYS",
          quantity: quantity_column || "ALWAYS",
          page_size: page_size || "A4",
        },
        hasChanged: false,
      });
    } else {
      // Only use defaults when no config exists

      setReceiptState({
        margin: { top: 1, bottom: 1, left: 1, right: 1 },
        others: {
          showUHID: false,
          showPaymentMethod: false,
          showReceiptNumber: true,
          requireUhid: false,
          print_amount_in_words: false,
          date_and_time: DateAndTimeConfigForReceipt.DATE_ONLY,
          bill_created_at: false,
          print_bill_created_by: false,
        },
        header: {
          headerHeight: 2.5,
          showHeader: true,
          headerImageSrc: "",
          headerImageFile: undefined,
        },
        footer: {
          footerHeight: 1.5,
          showFooter: true,
          footerImageSrc: "",
          footerImageFile: undefined,
          showPaymentId: false,
        },
        columns: {
          discount: "ALWAYS",
          quantity: "ALWAYS",
          page_size: "A4",
        },
        hasChanged: false,
      });
    }
  }, [receiptConfigData, currentDoctorId, clinicId, setReceiptState]);

  return (
    <div className="bg-neutral-0 dr-rounded-8 dr-h-full dr-overflow-y-scroll dr-relative">
      <CustomisationHeader
        showSelector={!isLoading}
        isInsideProfile={isInsideProfile}
      />
      {isLoading ? (
        <div className="flex dr-h-full justify-center items-center">
          <LoadingCircleIcon className="h-24 w-24 text-neutral-1000" />
        </div>
      ) : (
        <div className="divide-y-8 divide-darwin-neutral-50 dr-min-h-full">
          <HeaderAndFooter />
          <MarginsComponent />
          <ColumnsComponent />
          <OtherPreferencesComponent />
        </div>
      )}
      <SubmitAndResetComponent isInsideProfile={isInsideProfile} />
    </div>
  );
};

export default CustomisationTemplate;
